import { isArray } from 'lodash';
import {
    mergeClientSettings,
    type PartialPreconClientSettings,
} from './merge-settings';

export type PreconClientSettingsJson = PartialPreconClientSettings & {
    extends?: string | string[];
};

async function fetchSettingsJson(
    url: string | URL,
    strict = false,
    maxDepth = 10
): Promise<PartialPreconClientSettings> {
    let response: any;
    try {
        if (typeof window !== 'undefined') {
            response = await window.fetch(url);
        } else {
            throw new Error(`Unavailable for non-browser environments.`);
        }
    } catch (e) {
        console.warn(`Failed to load client settings from ${url}`, e);
        throw new Error(`resolve-client-settings-json-error:fetch`);
    }

    if (!response.ok) {
        console.warn(`Failed to load client settings from ${url}`, response);
        throw new Error(
            `resolve-client-settings-json-error:${response.status}`
        );
    }

    const data = (await response.json()) as PreconClientSettingsJson;

    const result = await resolveSettingsJson(data, strict, maxDepth);
    console.debug(`Resolved client settings from ${url}:`, result);
    return result;
}

export async function resolveSettingsJson(
    data: PreconClientSettingsJson,
    strict = false,
    maxDepth = 10
): Promise<PartialPreconClientSettings> {
    if (
        data.extends &&
        (typeof data.extends === 'string' || data.extends.length > 0)
    ) {
        if (maxDepth <= 0) {
            console.warn(`Max depth exceeded while loading extensions.`, data);
            if (strict) {
                throw new Error(
                    'resolve-clients-settings-json-error:max-extends-depth'
                );
            } else {
                return data;
            }
        }

        const extendsUrls = (
            isArray(data.extends) ? data.extends : [data.extends]
        ) as string[];
        const promises = extendsUrls.map((extendUrl) =>
            fetchSettingsJson(extendUrl, strict, maxDepth - 1)
        );

        let extendsResults: PartialPreconClientSettings[];
        if (strict) {
            extendsResults = await Promise.all(promises);
        } else {
            const settled = await Promise.allSettled(promises);
            extendsResults = settled
                .filter(
                    (
                        s
                    ): s is PromiseFulfilledResult<PartialPreconClientSettings> =>
                        s.status === 'fulfilled'
                )
                .map((s) => s.value);
        }

        return mergeClientSettings([...extendsResults, data]);
    } else {
        return data;
    }
}

export async function getSettingsJson(
    source: string | URL | PreconClientSettingsJson,
    strict = false,
    maxDepth = 10
): Promise<PartialPreconClientSettings> {
    try {
        if (typeof source === 'string' || source instanceof URL) {
            return await fetchSettingsJson(source, strict, maxDepth);
        } else {
            return await resolveSettingsJson(source, strict, maxDepth);
        }
    } catch (e) {
        if (strict) {
            throw e;
        } else {
            return {};
        }
    }
}
